import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useFeaturedReviews } from "../../hooks/useReviews";
import { displayName, formatScore } from "./reviewMath";
import art from "../../assets/reviews-tile-art.jpg";

const ROTATE_MS = 6000;
const STAR_PATH =
  "M10 1.6l2.55 5.3 5.85.82-4.25 4.1 1.03 5.8L10 14.86l-5.18 2.76 1.03-5.8L1.6 7.72l5.85-.82z";

const formatCount = (n) => Number(n || 0).toLocaleString("en-NG");

const reviewerName = (review) =>
  review.anonymous
    ? "Anonymous"
    : review.displayName || displayName(review.firstName, review.lastName);

const StarRowDark = ({ rating, size = 13 }) => {
  const pct = (Math.max(0, Math.min(5, rating || 0)) / 5) * 100;
  const row = (fill) =>
    Array.from({ length: 5 }, (_, i) => (
      <svg key={i} viewBox="0 0 20 20" width={size} height={size} fill={fill}>
        <path d={STAR_PATH} />
      </svg>
    ));
  return (
    <span className="stars" aria-hidden="true">
      <span className="s-bg">{row("currentColor")}</span>
      <span className="s-fg" style={{ width: `${pct}%` }}>
        {row("#FF7B1C")}
      </span>
    </span>
  );
};

const VerifiedTick = () => (
  <span className="verified">
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="currentColor" opacity=".22" />
      <path
        d="M4.6 8.3l2.2 2.2 4.6-4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span className="vt">Verified order</span>
  </span>
);

const useRotation = (count) => {
  const [index, setIndex] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    setIndex(0);
    if (count < 2) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    const timer = setInterval(() => {
      if (paused.current || document.hidden) return;
      setIndex((i) => (i + 1) % count);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [count]);

  const pause = () => {
    paused.current = true;
  };
  const resume = () => {
    paused.current = false;
  };

  return { index, pause, resume };
};

const ReviewsTile = ({ className = "" }) => {
  const { data, isLoading } = useFeaturedReviews();
  const reviews = data?.reviews || [];
  const score = data?.score || 0;
  const total = data?.total || 0;
  const { index, pause, resume } = useRotation(reviews.length);

  if (isLoading) {
    return (
      <div className={`vs-rv ${className}`}>
        <div className="tile" aria-hidden="true" />
      </div>
    );
  }

  const label = total
    ? `Rated ${formatScore(score)} out of 5 from ${formatCount(total)} Vshopper reviews. Read reviews.`
    : "Read Vshopper reviews.";

  return (
    <div className={`vs-rv ${className}`}>
      <Link
        to="/reviews"
        className="tile has-art"
        aria-label={label}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onFocus={pause}
        onBlur={resume}
      >
        <span
          className="c-art"
          aria-hidden="true"
          style={{ backgroundImage: `url(${art})` }}
        />
        <div className="c-left">
          {total > 0 && <div className="c-num">{formatScore(score)}</div>}
          {total > 0 && <StarRowDark rating={score} />}
          <div className="c-count">
            <span className="lbl">
              {total > 0 ? `${formatCount(total)} reviews` : "Reviews"}
            </span>{" "}
            <span className="arw" aria-hidden="true">
              →
            </span>
          </div>
        </div>
        <div className="c-right">
          {reviews.length ? (
            <div className="rot">
              {reviews.map((review, i) => (
                <figure
                  key={review.id || i}
                  className={`slide${i === index ? " on" : ""}`}
                >
                  <blockquote>“{review.comment.trim()}”</blockquote>
                  <figcaption>
                    <span className="nm">{reviewerName(review)}</span>
                    {review.verifiedOrder && <VerifiedTick />}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div className="c-empty">See what Vshoppers are saying</div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ReviewsTile;
